import React from "react";
import { useNavigate } from "react-router-dom";
import { X, Minus, Plus } from "lucide-react";
import { useCart } from "../context/CartContext";
import { useLang } from "../context/LanguageContext";
import { resolveImg } from "../lib/api";

const CartLine = ({ i, lang, t, money, updateQty, removeItem }) => (
  <div className="flex gap-4" data-testid={`cart-item-${i.slug}`}>
    <div className="w-20 h-24 bg-[#141311] flex-shrink-0 overflow-hidden">
      {i.image && <img src={resolveImg(i.image)} alt="" className="w-full h-full object-cover" />}
    </div>
    <div className="flex-1 min-w-0">
      <h3 className="serif text-lg leading-tight">{lang === "ar" ? i.name_ar || i.name_en : i.name_en}</h3>
      <p className="text-xs text-[#8A857D] mt-1">{i.size}</p>
      <div className="flex items-center justify-between mt-3">
        <div className="flex items-center border border-[#262421]">
          <button onClick={() => updateQty(i.product_id, i.qty - 1)} data-testid={`cart-dec-${i.slug}`} className="px-2 py-1 text-[#8A857D] hover:text-[#F2EBE1]"><Minus size={13} /></button>
          <span className="px-3 text-sm">{i.qty}</span>
          <button onClick={() => updateQty(i.product_id, i.qty + 1)} data-testid={`cart-inc-${i.slug}`} className="px-2 py-1 text-[#8A857D] hover:text-[#F2EBE1]"><Plus size={13} /></button>
        </div>
        <span className="text-sm text-[#C49A6C]">{money(i.price * i.qty)}</span>
      </div>
      <button onClick={() => removeItem(i.product_id)} data-testid={`cart-remove-${i.slug}`} className="text-[10px] uppercase tracking-widest text-[#6E685F] hover:text-[#c88] mt-2 transition-colors">{t("remove")}</button>
    </div>
  </div>
);

const EmptyBag = ({ t, go }) => (
  <div className="flex-1 flex flex-col items-center justify-center gap-6 px-6">
    <p className="text-[#8A857D]">{t("empty_bag")}</p>
    <button onClick={() => go("/shop")} className="text-xs uppercase tracking-[0.2em] border border-[#C49A6C] text-[#C49A6C] px-6 py-3 hover:bg-[#C49A6C] hover:text-[#0B0A08] transition-colors">{t("continue_shopping")}</button>
  </div>
);

const CartSummary = ({ t, money, subtotal, go }) => (
  <div className="p-6 border-t border-[#262421] space-y-4">
    <div className="flex justify-between text-sm">
      <span className="text-[#8A857D] uppercase tracking-widest text-xs">{t("subtotal")}</span>
      <span className="text-[#F2EBE1]">{money(subtotal)}</span>
    </div>
    <p className="text-[10px] text-[#6E685F] uppercase tracking-widest">{t("calculated_checkout")}</p>
    <button onClick={() => go("/checkout")} data-testid="cart-checkout-btn" className="w-full bg-[#C49A6C] text-[#0B0A08] text-xs uppercase tracking-[0.25em] py-4 hover:bg-[#E7CBA9] transition-colors">{t("checkout")}</button>
    <button onClick={() => go("/cart")} className="w-full text-xs uppercase tracking-[0.2em] text-[#8A857D] hover:text-[#F2EBE1] transition-colors">{t("your_bag")}</button>
  </div>
);

const hiddenShift = (lang) => (lang === "ar" ? "-translate-x-full" : "translate-x-full");

export const CartDrawer = () => {
  const { items, open, setOpen, updateQty, removeItem, subtotal } = useCart();
  const { t, lang, money } = useLang();
  const navigate = useNavigate();
  const go = (path) => { setOpen(false); navigate(path); };

  return (
    <div className={`fixed inset-0 z-[70] ${open ? "" : "pointer-events-none"}`}>
      <div className={`absolute inset-0 bg-black/70 transition-opacity duration-300 ${open ? "opacity-100" : "opacity-0"}`} onClick={() => setOpen(false)} />
      <div data-testid="cart-drawer"
        className={`absolute inset-y-0 end-0 w-full max-w-md bg-[#0F0F0F] border-s border-[#262421] flex flex-col transition-transform duration-400 ease-out ${open ? "translate-x-0" : hiddenShift(lang)}`}>
        <div className="flex items-center justify-between p-6 border-b border-[#262421]">
          <h2 className="serif text-2xl">{t("your_bag")}</h2>
          <button onClick={() => setOpen(false)} data-testid="cart-close" className="text-[#8A857D] hover:text-[#F2EBE1]"><X size={22} strokeWidth={1.4} /></button>
        </div>
        {items.length === 0 ? <EmptyBag t={t} go={go} /> : (
          <>
            <div className="flex-1 overflow-y-auto p-6 space-y-6">
              {items.map((i) => <CartLine key={i.product_id} i={i} lang={lang} t={t} money={money} updateQty={updateQty} removeItem={removeItem} />)}
            </div>
            <CartSummary t={t} money={money} subtotal={subtotal} go={go} />
          </>
        )}
      </div>
    </div>
  );
};
